import { ActionIcon, Badge, Button, Group, Kbd, Loader, Paper, SegmentedControl, Slider, Stack, Text, Tooltip } from "@mantine/core";
import { IconMap2, IconRotate360, IconWalk, IconZoomReset } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";

import { authFetch } from "../api/client";
import { http } from "../api/http";
import { useT } from "../i18n/script";

/** Zaglavlje LOD paketa (backend: orchard/pointcloud.py → viewer_package). */
export interface ViewerHeader {
  origin: [number, number, number];
  scale: [number, number, number];
  bounds: { min: number[]; max: number[] };
  levels: { name: string; count: number; bytes: number }[];
  has_rgb: boolean;
  classes: Record<string, string>;
  trees: { i: number; tree_id: number; code: string | null; x: number; y: number; z: number; h: number; crown_d: number; vol: number }[];
}

type ColorMode = "rgb" | "class" | "height" | "tree";
type NavMode = "orbit" | "walk";

const CLASS_COLORS: Record<number, [number, number, number]> = {
  1: [150, 150, 150], 2: [156, 120, 82], 3: [170, 214, 120], 4: [92, 170, 70], 5: [30, 110, 50], 6: [200, 80, 60], 7: [230, 40, 40],
};
const EYE = 1.7; // visina oka pri šetnji (m)
const CELL = 1.0; // ćelija modela terena (m)

interface Cloud {
  n: number;
  pos: Float32Array; // lokalne koordinate (x istok, y visina, z = -sjever)
  rgb: Uint8Array;
  cls: Uint8Array;
  tree: Uint16Array;
  hag: Float32Array; // visina iznad tla
}

function ramp(t: number): [number, number, number] {
  // perceptualno ujednačena skala (približno viridis)
  const s = [[68, 1, 84], [59, 82, 139], [33, 145, 140], [94, 201, 98], [253, 231, 37]];
  const x = Math.min(0.9999, Math.max(0, t)) * (s.length - 1);
  const i = Math.floor(x), f = x - i;
  return [0, 1, 2].map((k) => s[i][k] + (s[i + 1][k] - s[i][k]) * f) as [number, number, number];
}

function treeColor(i: number): [number, number, number] {
  const h = ((i * 137.508) % 360) / 360;
  const c = new THREE.Color().setHSL(h, 0.65, 0.55);
  return [c.r * 255, c.g * 255, c.b * 255];
}

export function PointCloudViewer({ pcId, onClose, onTree, selectedTree, height = "100%" }: {
  pcId: number; onClose?: () => void; onTree?: (treeId: number, code: string | null) => void; selectedTree?: number | null;
  height?: number | string;
}) {
  const { t } = useT();
  const host = useRef<HTMLDivElement>(null);
  const st = useRef<{
    renderer?: THREE.WebGLRenderer; scene?: THREE.Scene; camera?: THREE.PerspectiveCamera; orbit?: OrbitControls;
    points?: THREE.Points; cloud?: Cloud; header?: ViewerHeader; ground?: { nx: number; ny: number; x0: number; z0: number; g: Float32Array };
    center?: [number, number, number]; keys: Set<string>; yaw: number; pitch: number; nav: NavMode; marker?: THREE.Mesh;
    dirty: boolean;
  }>({ keys: new Set(), yaw: 0, pitch: 0, nav: "orbit", dirty: true });
  // crta se samo kada se nešto promijeni (štedi bateriju/GPU; bitno za 2,5 M tačaka)
  const invalidate = () => { st.current.dirty = true; };
  const [status, setStatus] = useState<string>("Učitavanje zaglavlja…");
  const [level, setLevel] = useState<string | null>(null);
  const [color, setColor] = useState<ColorMode>("rgb");
  const [nav, setNav] = useState<NavMode>("orbit");
  const [size, setSize] = useState(0.06);
  const [picked, setPicked] = useState<ViewerHeader["trees"][number] | null>(null);
  // najnovije vrijednosti za funkcije pozvane iz event-handlera i asinhronog učitavanja
  const live = useRef({ color, size, picked, onTree });
  live.current = { color, size, picked, onTree };

  // ------------------------------------------------------------ scena
  useEffect(() => {
    const el = host.current!;
    const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: "high-performance", preserveDrawingBuffer: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(el.clientWidth, el.clientHeight);
    el.appendChild(renderer.domElement);
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xdfe9f3);
    scene.fog = new THREE.Fog(0xdfe9f3, 150, 600);
    const camera = new THREE.PerspectiveCamera(60, el.clientWidth / el.clientHeight, 0.05, 3000);
    camera.position.set(0, 60, 80);
    const orbit = new OrbitControls(camera, renderer.domElement);
    orbit.enableDamping = true;
    orbit.maxPolarAngle = Math.PI * 0.495;
    orbit.addEventListener("change", invalidate);
    const marker = new THREE.Mesh(new THREE.SphereGeometry(0.25, 16, 12), new THREE.MeshBasicMaterial({ color: 0xffd400 }));
    marker.visible = false;
    scene.add(marker);
    Object.assign(st.current, { renderer, scene, camera, orbit, marker });

    const ro = new ResizeObserver(() => {
      renderer.setSize(el.clientWidth, el.clientHeight);
      camera.aspect = el.clientWidth / Math.max(1, el.clientHeight);
      camera.updateProjectionMatrix();
      invalidate();
    });
    ro.observe(el);

    // šetnja: WASD/strelice + povlačenje mišem za pogled
    const down = (e: KeyboardEvent) => { st.current.keys.add(e.key.toLowerCase()); };
    const up = (e: KeyboardEvent) => { st.current.keys.delete(e.key.toLowerCase()); };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    let drag: { x: number; y: number } | null = null;
    let moved = false;
    const pd = (e: PointerEvent) => { drag = { x: e.clientX, y: e.clientY }; moved = false; };
    const pm = (e: PointerEvent) => {
      if (!drag) return;
      const dx = e.clientX - drag.x, dy = e.clientY - drag.y;
      if (Math.abs(dx) + Math.abs(dy) > 3) moved = true;
      if (st.current.nav === "walk") {
        st.current.yaw -= dx * 0.004;
        st.current.pitch = Math.max(-1.3, Math.min(1.3, st.current.pitch - dy * 0.004));
        drag = { x: e.clientX, y: e.clientY };
        invalidate();
      }
    };
    const pu = (e: PointerEvent) => {
      if (drag && !moved) pick(e);
      drag = null;
    };
    renderer.domElement.addEventListener("pointerdown", pd);
    window.addEventListener("pointermove", pm);
    window.addEventListener("pointerup", pu);

    const timer = new THREE.Timer();
    let raf = 0;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      timer.update();
      const dt = Math.min(0.1, timer.getDelta());
      const s = st.current;
      if (s.nav === "walk") {
        const k = s.keys;
        const speed = (k.has("shift") ? 6 : 2.2) * dt;
        const fwd = new THREE.Vector3(-Math.sin(s.yaw), 0, -Math.cos(s.yaw));
        const right = new THREE.Vector3(-fwd.z, 0, fwd.x);
        if (k.has("w") || k.has("arrowup")) camera.position.addScaledVector(fwd, speed);
        if (k.has("s") || k.has("arrowdown")) camera.position.addScaledVector(fwd, -speed);
        if (k.has("a") || k.has("arrowleft")) camera.position.addScaledVector(right, -speed);
        if (k.has("d") || k.has("arrowright")) camera.position.addScaledVector(right, speed);
        if (["w", "a", "s", "d", "arrowup", "arrowdown", "arrowleft", "arrowright"].some((x) => k.has(x))) s.dirty = true;
        camera.position.y = groundAt(camera.position.x, camera.position.z) + EYE;
        camera.rotation.set(s.pitch, s.yaw, 0, "YXZ");
      } else if (orbit.update()) s.dirty = true;
      if (!s.dirty) return;
      s.dirty = false;
      renderer.render(scene, camera);
    };
    loop();
    return () => {
      cancelAnimationFrame(raf);
      timer.dispose();
      ro.disconnect();
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("pointermove", pm);
      window.removeEventListener("pointerup", pu);
      orbit.dispose();
      st.current.points?.geometry.dispose();
      (st.current.points?.material as THREE.Material | undefined)?.dispose();
      renderer.dispose();
      el.removeChild(renderer.domElement);
      st.current.points = undefined;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------------------------------------ podaci (LOD: l0 brzo, zatim l1 puni detalj)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const h = await http.get<ViewerHeader>(`/api/v1/pointclouds/${pcId}/viewer.json`);
        if (cancelled) return;
        st.current.header = h;
        const c = [(h.bounds.min[0] + h.bounds.max[0]) / 2, (h.bounds.min[1] + h.bounds.max[1]) / 2, h.bounds.min[2]] as [number, number, number];
        st.current.center = c;
        for (const lv of h.levels) {
          setStatus(`${t("Učitavanje")} ${lv.name === "l0" ? t("pregleda") : t("punog detalja")} (${(lv.count / 1e6).toFixed(2)} M ${t("tačaka")})…`);
          const r = await authFetch(`/api/v1/pointclouds/${pcId}/viewer/${lv.name}.bin`);
          if (!r.ok) throw new Error(`${r.status}`);
          const buf = await r.arrayBuffer();
          if (cancelled) return;
          build(decode(buf, lv.count, h, c), lv.name === h.levels[0].name);
          setLevel(lv.name);
        }
        setStatus("");
      } catch (e) {
        setStatus(`${t("Greška pri učitavanju 3D snimka")}: ${String(e)}`);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pcId]);

  function decode(buf: ArrayBuffer, n: number, h: ViewerHeader, c: [number, number, number]): Cloud {
    const q = new Uint16Array(buf, 0, n * 3);
    const rgb = new Uint8Array(buf, n * 6, n * 3);
    const cls = new Uint8Array(buf, n * 9, n);
    const tree = new Uint16Array(buf.slice(n * 10, n * 12));
    const pos = new Float32Array(n * 3);
    const [ox, oy, oz] = h.origin, [sx, sy, sz] = h.scale;
    for (let i = 0; i < n; i++) {
      const x = ox + q[i * 3] * sx, y = oy + q[i * 3 + 1] * sy, z = oz + q[i * 3 + 2] * sz;
      pos[i * 3] = x - c[0];
      pos[i * 3 + 1] = z - c[2];
      pos[i * 3 + 2] = -(y - c[1]);
    }
    // model terena (najniža tačka tla u ćeliji 1 m) — za šetnju i boju po visini iznad tla
    const x0 = h.bounds.min[0] - c[0], z0 = -(h.bounds.max[1] - c[1]);
    const nx = Math.ceil((h.bounds.max[0] - h.bounds.min[0]) / CELL) + 1, ny = Math.ceil((h.bounds.max[1] - h.bounds.min[1]) / CELL) + 1;
    const g = new Float32Array(nx * ny).fill(NaN);
    for (let i = 0; i < n; i++) {
      if (cls[i] !== 2) continue;
      const ix = Math.floor((pos[i * 3] - x0) / CELL), iz = Math.floor((pos[i * 3 + 2] - z0) / CELL);
      const k = iz * nx + ix;
      if (!(g[k] <= pos[i * 3 + 1])) g[k] = pos[i * 3 + 1];
    }
    fillHoles(g, nx, ny);
    st.current.ground = { nx, ny, x0, z0, g };
    const hag = new Float32Array(n);
    for (let i = 0; i < n; i++) hag[i] = pos[i * 3 + 1] - groundAt(pos[i * 3], pos[i * 3 + 2]);
    return { n, pos, rgb, cls, tree, hag };
  }

  function fillHoles(g: Float32Array, nx: number, ny: number) {
    let mean = 0, cnt = 0;
    g.forEach((v) => { if (!Number.isNaN(v)) { mean += v; cnt++; } });
    mean = cnt ? mean / cnt : 0;
    for (let pass = 0; pass < 6; pass++) {
      let left = 0;
      for (let j = 0; j < ny; j++) for (let i = 0; i < nx; i++) {
        const k = j * nx + i;
        if (!Number.isNaN(g[k])) continue;
        let s = 0, c = 0;
        for (const [di, dj] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
          const ii = i + di, jj = j + dj;
          if (ii < 0 || jj < 0 || ii >= nx || jj >= ny) continue;
          const v = g[jj * nx + ii];
          if (!Number.isNaN(v)) { s += v; c++; }
        }
        if (c) g[k] = s / c; else left++;
      }
      if (!left) break;
    }
    for (let k = 0; k < g.length; k++) if (Number.isNaN(g[k])) g[k] = mean;
  }

  function groundAt(x: number, z: number): number {
    const gr = st.current.ground;
    if (!gr) return 0;
    const ix = Math.min(gr.nx - 1, Math.max(0, Math.floor((x - gr.x0) / CELL)));
    const iz = Math.min(gr.ny - 1, Math.max(0, Math.floor((z - gr.z0) / CELL)));
    return gr.g[iz * gr.nx + ix];
  }

  function colors(c: Cloud, mode: ColorMode, hiTree = 0): Float32Array {
    const out = new Float32Array(c.n * 3);
    const maxH = Math.max(3, ...(st.current.header?.trees.map((x) => x.h) ?? [4]).slice(0, 2000));
    for (let i = 0; i < c.n; i++) {
      let r: number, g: number, b: number;
      if (hiTree && c.tree[i] === hiTree) { r = 255; g = 212; b = 0; }
      else if (mode === "rgb") { r = c.rgb[i * 3]; g = c.rgb[i * 3 + 1]; b = c.rgb[i * 3 + 2]; }
      else if (mode === "class") [r, g, b] = CLASS_COLORS[c.cls[i]] ?? [120, 120, 120];
      else if (mode === "height") [r, g, b] = c.cls[i] === 2 ? [150, 130, 105] : ramp(c.hag[i] / maxH);
      else[r, g, b] = c.tree[i] ? treeColor(c.tree[i]) : c.cls[i] === 2 ? [165, 150, 130] : [200, 200, 200];
      out[i * 3] = r / 255; out[i * 3 + 1] = g / 255; out[i * 3 + 2] = b / 255;
    }
    return out;
  }

  function build(c: Cloud, first: boolean) {
    const s = st.current;
    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.BufferAttribute(c.pos, 3));
    geom.setAttribute("color", new THREE.BufferAttribute(colors(c, live.current.color, live.current.picked?.i ?? 0), 3));
    geom.computeBoundingSphere();
    const mat = new THREE.PointsMaterial({ size: live.current.size, vertexColors: true, sizeAttenuation: true });
    if (s.points) {
      s.scene!.remove(s.points);
      s.points.geometry.dispose();
      (s.points.material as THREE.Material).dispose();
    }
    s.points = new THREE.Points(geom, mat);
    s.cloud = c;
    s.scene!.add(s.points);
    if (first) resetView();
    invalidate();
  }

  function resetView() {
    const s = st.current;
    const h = s.header;
    if (!h || !s.camera || !s.orbit) return;
    const w = h.bounds.max[0] - h.bounds.min[0], d = h.bounds.max[1] - h.bounds.min[1];
    const r = Math.max(w, d);
    s.camera.position.set(r * 0.35, r * 0.55, r * 0.75);
    s.orbit.target.set(0, 2, 0);
    s.orbit.update();
    invalidate();
  }

  // ------------------------------------------------------------ izbor stabla klikom
  function pick(e: PointerEvent) {
    const s = st.current;
    if (!s.points || !s.camera || !s.renderer || !s.cloud || !s.header) return;
    const rect = s.renderer.domElement.getBoundingClientRect();
    const m = new THREE.Vector2(((e.clientX - rect.left) / rect.width) * 2 - 1, -((e.clientY - rect.top) / rect.height) * 2 + 1);
    const rc = new THREE.Raycaster();
    rc.params.Points = { threshold: 0.15 };
    rc.setFromCamera(m, s.camera);
    const hits = rc.intersectObject(s.points);
    const hit = hits.find((hh) => hh.index !== undefined && s.cloud!.tree[hh.index] > 0);
    if (!hit) { setPicked(null); return; }
    const ti = s.cloud.tree[hit.index!];
    const tr = s.header.trees.find((x) => x.i === ti) ?? null;
    setPicked(tr);
    if (tr) live.current.onTree?.(tr.tree_id, tr.code);
  }

  // ------------------------------------------------------------ promjene prikaza
  useEffect(() => {
    const s = st.current;
    if (!s.points || !s.cloud) return;
    s.points.geometry.setAttribute("color", new THREE.BufferAttribute(colors(s.cloud, color, picked?.i ?? 0), 3));
    if (s.marker && s.center && picked) {
      s.marker.position.set(picked.x - s.center[0], picked.z - s.center[2] + 0.4, -(picked.y - s.center[1]));
      s.marker.visible = true;
    } else if (s.marker) s.marker.visible = false;
    invalidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color, picked, level]);

  useEffect(() => {
    const s = st.current;
    if (s.points) (s.points.material as THREE.PointsMaterial).size = size;
    invalidate();
  }, [size, level]);

  // spolja izabrano stablo (npr. iz tabele) — označi ga i dovedi kameru
  useEffect(() => {
    const s = st.current;
    if (!selectedTree || !s.header) return;
    const tr = s.header.trees.find((x) => x.tree_id === selectedTree);
    if (!tr || tr.i === picked?.i) return;
    setPicked(tr);
    if (s.center && s.orbit && s.camera && s.nav === "orbit") {
      const p = new THREE.Vector3(tr.x - s.center[0], tr.z - s.center[2] - tr.h / 2, -(tr.y - s.center[1]));
      s.orbit.target.copy(p);
      s.camera.position.set(p.x + 6, p.y + 5, p.z + 8);
      invalidate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTree, level]);

  function setNavMode(m: NavMode) {
    const s = st.current;
    s.nav = m;
    setNav(m);
    if (!s.camera || !s.orbit) return;
    if (m === "walk") {
      s.orbit.enabled = false;
      // start na ivici voćnjaka, pogled ka centru
      const tgt = s.orbit.target.clone();
      const dir = new THREE.Vector3().subVectors(tgt, s.camera.position).setY(0).normalize();
      const start = picked && s.center
        ? new THREE.Vector3(picked.x - s.center[0] - 2.5, 0, -(picked.y - s.center[1]) + 2.5)
        : new THREE.Vector3(tgt.x - dir.x * 10, 0, tgt.z - dir.z * 10);
      s.camera.position.set(start.x, groundAt(start.x, start.z) + EYE, start.z);
      s.yaw = Math.atan2(-dir.x, -dir.z);
      s.pitch = 0;
      invalidate();
    } else {
      s.orbit.enabled = true;
      resetView();
    }
  }

  const h = st.current.header;
  return (
    <div style={{ position: "relative", height, width: "100%", overflow: "hidden", background: "#dfe9f3" }}>
      <div ref={host} style={{ position: "absolute", inset: 0 }} />
      <Paper shadow="md" p="xs" style={{ position: "absolute", top: 12, left: 12, zIndex: 2 }}>
        <Stack gap={6}>
          <Group gap={6}>
            {onClose && (
              <Button size="xs" leftSection={<IconMap2 size={14} />} onClick={onClose}>{t("2D")}</Button>
            )}
            <SegmentedControl size="xs" value={nav} onChange={(v) => setNavMode(v as NavMode)} data={[
              { value: "orbit", label: <Group gap={4} wrap="nowrap"><IconRotate360 size={14} />{t("Orbita")}</Group> },
              { value: "walk", label: <Group gap={4} wrap="nowrap"><IconWalk size={14} />{t("Šetnja")}</Group> },
            ]} />
            <Tooltip label={t("Početni pogled")}>
              <ActionIcon variant="light" onClick={() => setNavMode("orbit")}><IconZoomReset size={16} /></ActionIcon>
            </Tooltip>
          </Group>
          <SegmentedControl size="xs" value={color} onChange={(v) => setColor(v as ColorMode)} data={[
            { value: "rgb", label: t("Boja") }, { value: "class", label: t("Klase") },
            { value: "height", label: t("Visina") }, { value: "tree", label: t("Stabla") },
          ]} />
          <Group gap={6} wrap="nowrap">
            <Text size="xs" c="dimmed" w={70}>{t("Veličina")}</Text>
            <Slider size="xs" style={{ flex: 1 }} min={0.02} max={0.2} step={0.01} value={size} onChange={setSize}
              label={(v) => `${(v * 100).toFixed(0)} cm`} />
          </Group>
          {nav === "walk" && (
            <Text size="xs" c="dimmed"><Kbd>W</Kbd><Kbd>A</Kbd><Kbd>S</Kbd><Kbd>D</Kbd> {t("kretanje")}, <Kbd>Shift</Kbd> {t("brže")}, {t("povlačenje mišem — pogled")}</Text>
          )}
          {color === "class" && h && (
            <Group gap={4}>
              {Object.entries(h.classes).filter(([k]) => ["2", "3", "4", "5"].includes(k)).map(([k, v]) => (
                <Badge key={k} size="xs" variant="filled" style={{ background: `rgb(${CLASS_COLORS[Number(k)].join(",")})` }}>{t(v)}</Badge>
              ))}
            </Group>
          )}
          {color === "height" && <Text size="xs" c="dimmed">{t("Visina iznad tla: ljubičasto 0 m → žuto vrh krošnje")}</Text>}
        </Stack>
      </Paper>
      <Paper shadow="xs" px="xs" py={4} style={{ position: "absolute", bottom: 10, left: 12, zIndex: 2, opacity: 0.9 }}>
        <Text size="xs" c="dimmed">
          {h ? `${h.trees.length} ${t("stabala")} · ${level === "l1" ? t("puni detalj") : t("pregled")} · EPSG:8678` : ""}
          {" · "}{t("klik na krošnju = karton stabla")}
        </Text>
      </Paper>
      {picked && !onTree && (
        <Paper shadow="md" p="xs" style={{ position: "absolute", top: 12, right: 12, zIndex: 2 }}>
          <Text fw={600}>{picked.code}</Text>
          <Text size="xs">{t("Visina")}: {picked.h?.toFixed(2)} m · {t("Krošnja")}: {picked.crown_d?.toFixed(2)} m · {picked.vol?.toFixed(2)} m³</Text>
        </Paper>
      )}
      {status && (
        <Paper shadow="md" p="md" style={{ position: "absolute", top: "45%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 3 }}>
          <Group>{!status.startsWith(t("Greška")) && <Loader size="sm" />}<Text size="sm">{t(status)}</Text></Group>
        </Paper>
      )}
    </div>
  );
}
